import { useEffect, useState } from "react";
import { createLogger } from "@/logging";
import { loadCollectionBand, type CollectionBandState } from "./diagnosticsSeries";

const logger = createLogger("components/dataset/diagnostics");

/**
 * 데이터셋 진단의 기준 밴드를 참고용 오버레이로 1회 로드한다.
 *
 * 밴드는 "Parquet 미리보기"의 오버레이 소스일 뿐이고 화면을 막지 않는다. 그래서 조회 실패는
 * 모두 `null` 로 처리한다. 그러면 소비부는 에피소드 시리즈만 그린다.
 * - `enabled=false`(예: 상세 탭)면 조회하지 않고 `null` 을 반환한다.
 * - `datasetUuid` 가 바뀌면 이전 밴드를 즉시 버린다. 그래서 이전 컬렉션의 stale 밴드가
 *   남지 않는다.
 */
export function useCollectionDiagnostics(
  datasetUuid: string,
  enabled: boolean,
): CollectionBandState | null {
  const [state, setState] = useState<CollectionBandState | null>(null);

  useEffect(() => {
    if (!enabled || !datasetUuid) {
      setState(null);
      return;
    }
    let cancelled = false;
    // 데이터셋을 바꾸면 이전 밴드를 즉시 버린다. 새 조회가 도착하기 전에 stale 밴드를 표시하지 않는다.
    setState(null);
    void loadCollectionBand(datasetUuid)
      .then((data) => {
        if (!cancelled) setState(data);
      })
      .catch((error: unknown) => {
        // 참고용 오버레이라서 조회가 실패하면 밴드를 숨긴다. 404 는 로더가 이미 degrade 하고,
        // 권한·서버 오류도 여기서 처리한다. 미리보기 자체는 에피소드 시리즈로 계속 동작한다.
        if (cancelled) return;
        logger.warn("loadCollectionBand failed; hiding band", error);
        setState(null);
      });
    return () => {
      cancelled = true;
    };
  }, [datasetUuid, enabled]);

  return state;
}
